/*
 * UTC offer cart guard. A Shopify cart validation rejects (HTTP 422) any cart
 * with a gift line (property _gift=utc3|hb3|meno3 or an ebook) and no anchor (a 3 Pack
 * of UTC, ph-gut-balance, HB, Peri/Meno or their Protocols on an "Every 3 months" plan),
 * or a gift with qty > 1.
 * Every path here that removes or changes an anchor clears the gift lines in
 * the SAME /cart/update.js call (or first, when only /cart/change.js can do
 * the change), so the cart never passes through an invalid state.
 */
var UtcCartGuard = window.UtcCartGuard || (function () {
  // Offer keys written to _gift/_offer by the offer box (UTC, Hormone Balance, Peri/Meno).
  var OFFER_KEYS = ['utc3', 'hb3', 'meno3'];
  // Ebook gift handles -> offer key (HB / Meno ebooks: add their handles once the products exist).
  var EBOOK_OFFER = {
    'bladder-reset-guide-ebook': 'utc3',
    'hormone-balance-handbook-ebook': 'hb3'
  };
  var EBOOKS = Object.keys(EBOOK_OFFER);
  // Anchor product handle -> offer key. Removing an anchor only clears gifts of ITS offer.
  var HANDLE_OFFER = {
    'urinary-tract-cleanse-protect': 'utc3', 'ph-gut-balance': 'utc3',
    'hormone-balance': 'hb3', 'hormonal-gut-balance': 'hb3',
    'menopause-essentials': 'meno3', 'bundle-40plus-essentials': 'meno3'
  };
  var HANDLES = Object.keys(HANDLE_OFFER);
  var FALLBACK = 'We could not update your cart. Please try again.';
  function isGift(i) {
    return !!(i && ((i.properties && OFFER_KEYS.indexOf(i.properties._gift) >= 0) || EBOOKS.indexOf(i.handle) >= 0));
  }
  function isAnchor(i) {
    if (!i || HANDLES.indexOf(i.handle) < 0) return false;
    var a = i.selling_plan_allocation;
    var plan = (a && a.selling_plan && a.selling_plan.name) || '';
    return /3\s*pack/i.test(i.variant_title || '') && /3\s*month/i.test(plan);
  }
  function report(msg) {
    document.dispatchEvent(new CustomEvent('lp-utc-offer:error', { detail: { message: msg || FALLBACK } }));
  }
  async function call(url, body) {
    var res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    var data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok) {
      var err = new Error(url + ' ' + res.status);
      err.description = (data && (data.description || data.message)) || FALLBACK;
      throw err;
    }
    return data;
  }
  async function getCart() {
    var res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }
  // Gift line keys that must go to 0 if the lines in `keys` are removed or stop being anchors.
  // Per offer: an hb3 anchor leaving clears only hb3 gifts, never utc3 ones (and the reverse).
  function giftOffer(i) {
    var g = i && i.properties && i.properties._gift;
    if (g && OFFER_KEYS.indexOf(g) >= 0) return g;
    return (i && EBOOK_OFFER[i.handle]) || '';
  }
  function giftKeysIfAnchorsLeave(items, keys) {
    items = items || [];
    var out = [];
    var leaving = {};
    items.forEach(function (i) { if (keys.indexOf(i.key) >= 0 && isAnchor(i)) leaving[HANDLE_OFFER[i.handle]] = true; });
    Object.keys(leaving).forEach(function (offer) {
      var stays = items.some(function (i) { return keys.indexOf(i.key) < 0 && isAnchor(i) && HANDLE_OFFER[i.handle] === offer; });
      if (stays) return;
      items.forEach(function (i) { if (isGift(i) && giftOffer(i) === offer && out.indexOf(i.key) < 0) out.push(i.key); });
    });
    return out;
  }
  // Remove lines (qty 0) plus any gifts that would be orphaned, in one update.js call.
  async function removeLines(keys) {
    var cart = await getCart();
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    giftKeysIfAnchorsLeave(cart.items, keys).forEach(function (k) { updates[k] = 0; });
    return call('/cart/update.js', { updates: updates });
  }
  // Before an anchor is changed to a non-anchor via change.js, clear its gifts first.
  async function clearGiftsBeforeAnchorChange(key) {
    var cart = await getCart();
    var gk = giftKeysIfAnchorsLeave(cart.items, [key]);
    if (!gk.length) return null;
    var updates = {};
    gk.forEach(function (k) { updates[k] = 0; });
    return call('/cart/update.js', { updates: updates });
  }
  return {
    OFFER_KEYS: OFFER_KEYS, isGift: isGift, isAnchor: isAnchor, report: report, call: call, getCart: getCart,
    giftKeysIfAnchorsLeave: giftKeysIfAnchorsLeave, removeLines: removeLines,
    clearGiftsBeforeAnchorChange: clearGiftsBeforeAnchorChange, FALLBACK: FALLBACK
  };
})();
window.UtcCartGuard = UtcCartGuard;

// Order Summary math. anchor_unit = variant compare_at if > variant price, else the
// variant ONE-TIME price (subscription lines carry the plan price, so the one-time
// price comes from vmap = { [variantId]: { price, compare_at_price } } built from
// /products/<handle>.js). Falls back to the line's own original_price if unmapped.
function semaineCartSavings(cart, vmap) {
  vmap = vmap || {};
  // Match checkout's "Total savings": original_total_price - total_price (gifts at real price).
  if (cart && typeof cart.original_total_price === 'number') {
    var cpay = cart.total_price || 0;
    var csave = cart.original_total_price - cpay;
    return { original: cart.original_total_price, pay: cpay, save: csave > 0 ? csave : 0, show: csave > 0 };
  }
  var original = 0;
  ((cart && cart.items) || []).forEach(function (i) {
    var v = vmap[i.id];
    var unit = i.original_price || i.price || 0;
    var isGift = i.properties && i.properties._gift;
    if (v && v.price != null) {
      // Gifts are valued at their real price (matches the offer box).
      unit = (!isGift && v.compare_at_price && v.compare_at_price > v.price) ? v.compare_at_price : v.price;
    }
    original += unit * (i.quantity || 0);
  });
  var pay = (cart && cart.total_price) || 0;
  var save = original - pay;
  return { original: original, pay: pay, save: save > 0 ? save : 0, show: save > 0 };
}
window.semaineCartSavings = semaineCartSavings;
if (typeof module !== 'undefined') module.exports = { semaineCartSavings: semaineCartSavings };

document.addEventListener('alpine:init', () => {
  Alpine.data('CartDrawer', () => ({
    cart: {
      items: [], 
      item_count: 0, 
      total_price: 0
    },
    flags: {
      open: false,
      empty: true,
      enable_recommended_upsells: false,
      discount_code_applied: false
    },
    discount_code: '20OFF',
    recommended_products: [],
    // UTC offer (variant arm only). Bumped when assets/lp-utc-offer-gifts.js
    // finishes loading so the getters below re-evaluate.
    offerTick: 0,
    offerError: '',
    variantPrices: {},
    // Recommendations minus anything already in the cart (by product id or handle).
    get upsellProducts() {
      let items = (this.cart && this.cart.items) || [];
      let ids = items.map((i) => String(i.product_id));
      let handles = items.map((i) => i.handle);
      return (this.recommended_products || []).filter((p) => ids.indexOf(String(p.id)) === -1 && handles.indexOf(p.handle) === -1);
    },
    get orderSummary() {
      return semaineCartSavings(this.cart, this.variantPrices);
    },
    async LoadVariantPrices(cart) {
      let am = this;
      window.__semaine_product_cache = window.__semaine_product_cache || {};
      let cache = window.__semaine_product_cache;
      let handles = [...new Set(cart.items.map((i) => i.handle).filter(Boolean))];
      try {
        let products = await Promise.all(handles.map((h) => {
          if (!cache[h]) cache[h] = fetch(`/products/${h}.js`).then((r) => r.json());
          return cache[h];
        }));
        let map = {};
        products.forEach((p) => (p.variants || []).forEach((v) => { map[v.id] = { price: v.price, compare_at_price: v.compare_at_price }; }));
        am.variantPrices = map;
      } catch (e) { /* fall back to line original_price */ }
    },
    init() {
      this.InitWatchers();
      this.InitSelf();
      this.InitEventHandlers();
    },
    InitSelf() {
      let am = this;
      am.UpdateCart();
      am.flags.enable_recommended_upsells = am.$el.dataset.enable_recommended_upsells == 'true';
    },
    InitWatchers() {
      let am = this;
      am.$watch('flags.open', () => {
        // .is-cart-open clamps <main> to 100dvh (assets/_custom.css), which
        // forces scrollY to 0 while the drawer is open. Save the position
        // before the class lands and restore it once the class comes off.
        if (am.flags.open) am._scrollY = window.scrollY;
        document.body.classList.toggle('is-cart-open', am.flags.open);
        if (!am.flags.open && am._scrollY != null) {
          window.scrollTo(0, am._scrollY);
          am._scrollY = null;
        }
      });

      am.$watch('cart', am.UpdateFlags.bind(am));

      // Opening is a separate, explicit signal. cart:modified only means
      // cart state changed and must never infer user intent.
      document.addEventListener('cart:drawer:open', () => {
        am.flags.open = true;
      });

      document.addEventListener('cart:modified', async (e) => {
        await am.UpdateCart();
      });

      UTILS.ObserveMutation('#cart-icon-bubble', {}, async(m) => {
        // Keep state in sync, but do not infer add-to-cart intent from a
        // cart-icon DOM mutation caused by theme or app rendering.
        await am.UpdateCart();
      });

      document.addEventListener('cart:drawer:close', () => {
        am.flags.open = false;
      });

      document.addEventListener('lp-utc-offer:ready', () => {
        am.offerTick++;
      });
      document.addEventListener('lp-utc-offer:error', (e) => {
        am.offerError = (e && e.detail && e.detail.message) || UtcCartGuard.FALLBACK;
      });
    },
    InitEventHandlers() {
      let am = this;
      let $toggles = [
        ...document.querySelectorAll('#cart-icon-bubble')
      ];
      // console.log($toggles)
      $toggles.forEach(($toggle) => {
        $toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          am.flags.open = true;
        });
      });
    },
    FormatMoney(value) {
      return UTILS.FormatMoney(value);
    },
    UpdateFlags() {
      let am = this;
      am.flags.empty = am.cart.item_count == 0;
      am.flags.discount_code_applied = am.cart.cart_level_discount_applications?.find(item => item.title == am.discount_code) !== undefined;
    },
    async UpdateCart() {
      let am = this;
      let cart = await fetch('/cart.js').then(res => res.json());
      am.ApplyCart(cart);
    },
    // Apply a freshly-fetched cart object to Alpine state. Extracted from
    // UpdateCart so mutation handlers (UpdateLineQuantity, AddUpsell, Revert*)
    // can re-use the cart object that Shopify already returns from /cart/update.js
    // and /cart/change.js — no need to fire a follow-up GET /cart.js round-trip.
    ApplyCart(cart) {
      let am = this;
      cart.items = cart.items.reverse(); // reverse to show items in the order they were added

      // UTC offer (variant arm only): carts without _offer/_gift lines skip all of this.
      if (cart.items.some((i) => am.IsOfferLine(i) || am.isGiftLine(i))) {
        am.EnsureOfferScript();
        // keep gift lines under the main line
        if (cart.items.some((i) => am.isGiftLine(i))) {
          cart.items = cart.items.slice().sort((a, b) => (am.isGiftLine(a) ? 1 : 0) - (am.isGiftLine(b) ? 1 : 0));
        }
      }

      am.$nextTick(() => {
        am.cart = {...cart};
        document.dispatchEvent(new CustomEvent('cart:updated', {detail: { cart }}));
      });
      am.LoadVariantPrices(cart);
      document.querySelectorAll('[data-cart-count]').forEach(($el) => {
        $el.textContent = cart.item_count;
      });
    },
    // ---- UTC offer helpers (variant arm only; no-ops for every other cart) ----
    IsOfferLine(item) {
      return !!(item && item.properties && UtcCartGuard.OFFER_KEYS.indexOf(item.properties._offer) >= 0);
    },
    isGiftLine(item) {
      return !!(item && item.properties && UtcCartGuard.OFFER_KEYS.indexOf(item.properties._gift) >= 0);
    },
    EnsureOfferScript() {
      let am = this;
      if (window.LPUtcOffer || am._offerLoading) return;
      let src = am.$el && am.$el.dataset ? am.$el.dataset.offerGiftsSrc : '';
      if (!src) return;
      am._offerLoading = true;
      let script = document.createElement('script');
      script.src = src;
      script.defer = true;
      document.head.appendChild(script);
    },
    get offerActive() {
      void this.offerTick;
      return (this.cart.items || []).some((i) => this.IsOfferLine(i) || this.isGiftLine(i));
    },
    get offerHasSub() {
      return (this.cart.items || []).some((i) => !!i.selling_plan_allocation);
    },
    get offerSavings() {
      void this.offerTick;
      // Same number checkout shows as "Total savings" (see semaineCartSavings).
      return this.offerActive ? semaineCartSavings(this.cart, this.variantPrices).save : 0;
    },
    get offerNudgeData() {
      void this.offerTick;
      let o = window.LPUtcOffer;
      return o && this.offerActive ? o.nudge(this.cart) : null;
    },
    get offerRenewal() {
      void this.offerTick;
      let o = window.LPUtcOffer;
      return o && this.offerActive ? o.renewal(this.cart) : '';
    },
    giftMeta(item) {
      let kind = item && item.properties ? item.properties._gift_kind : '';
      return kind === 'ebook' ? 'Gift \u00b7 emailed after checkout' : 'Gift \u00b7 ships with your first order';
    },
    // Struck value for a gift line: compare-at if set, else price (gifts are paid products discounted to 0 at checkout).
    giftStrike(item) {
      void this.offerTick;
      let o = window.LPUtcOffer;
      // Single struck price: the variant's real price.
      if (item.original_line_price > 0) return item.original_line_price;
      let v = o && o.lineValue ? o.lineValue(item) : 0;
      return v || 0;
    },
    // Struck one-time value for an offer line (0 when nothing to strike).
    offerStrike(item) {
      void this.offerTick;
      let o = window.LPUtcOffer;
      if (!o || this.isGiftLine(item) || !this.IsOfferLine(item) || !item.selling_plan_allocation) return 0;
      let value = o.lineValue(item);
      return value > item.final_line_price ? value : 0;
    },
    async e_OfferSwitch(key) {
      let o = window.LPUtcOffer;
      if (!o) return;
      this.offerError = '';
      try { await o.switchTo3(key); }
      catch (e) {
        this.offerError = (e && e.description) || UtcCartGuard.FALLBACK;
        await this.UpdateCart();
      }
    },
    async e_UpdateLineQuantity(item) {
      let am = this;
      am.offerError = '';
      let qty = parseInt(item.quantity, 10);
      if (isNaN(qty)) { await am.UpdateCart(); return; }
      qty = Math.max(0, qty);
      // Gifts are always exactly one: clamp any attempt to raise the quantity.
      if (UtcCartGuard.isGift(item) && qty > 1) qty = 1;
      // A shopper-removed gift must not be re-added this session.
      if (qty === 0 && am.isGiftLine(item) && window.LPUtcOffer) {
        window.LPUtcOffer.markRemoved(item);
      }
      let item_updates = {};
      item_updates[item.key] = qty;
      // Removing the last anchor takes its gifts with it in the same call.
      if (qty === 0) {
        UtcCartGuard.giftKeysIfAnchorsLeave(am.cart.items, [item.key]).forEach((k) => { item_updates[k] = 0; });
      }
      try {
        // /cart/update.js returns the full updated cart — use it directly
        // instead of awaiting then firing a second GET /cart.js.
        let cart = await UtcCartGuard.call('/cart/update.js', { updates: item_updates });
        am.ApplyCart(cart);
      } catch (e) {
        am.offerError = (e && e.description) || UtcCartGuard.FALLBACK;
        await am.UpdateCart();
      }
    },
    e_Checkout() {
      window.location.href = '/checkout';
    },
    async e_LoadRecommendedProducts(e) {
      // console.log(e);
      let am = this;
       //await window.shoplift.setAnalyticsConsent(true);
        //console.log(window.shoplift);
       //const skipUpsells = await window.shoplift.isHypothesisActive('019bbda1-cde3-746d-a32e-2481d762729e').then(result => console.log('In variant:', result));
        // Verify Shoplift is loaded

      //if (skipUpsells) return;

      // if (!am.flags.enable_recommended_upsells) return;
      let recommended_products = [...new Set(e.detail.products)];
      recommended_products.forEach((p) => {
        p.variant_first = p.variants.find((v) => v.available);
      });

      let section = 'product-mf';
      let px = Promise.all(recommended_products.map(async (p) => {  
        let p_res = await fetch(`/products/${p.handle}?sections=${section}`).then(res => res.json());
        p.image_1_unit = UTILS.ConvertStringToHTML(p_res[section]).querySelector('[data-product_metafields]').dataset.image_1_unit;
        //commented to remove the overwriting of the default image

        return p;
      }));

      let products = await px;
      am.recommended_products = [...products].slice(0, 2);
    },
    async e_ToggleCoupon() {
      await fetch(`/discount/${this.discount_code}`);
      document.dispatchEvent(new CustomEvent('cart:modified'));
    }
  }));

  Alpine.data('CartProgressBars', () => ({
    pbars: {
      free_product: {
        enabled: true,
        threshold: 4500,
        progress: 0
      },
      free_ship: {
        enabled: true,
        threshold: 10000,
        progress: 0
      }
    },
    init() {
      this.InitSelf();
      this.InitWatchers();
      this.UpdateProgress();
    },
    InitSelf() {
      let am = this;
      am.pbars.free_ship.threshold = parseFloat(am.$el.dataset.freeShipThreshold) * 100;
    },
    InitWatchers() {
      let am = this;
      am.$watch('cart', am.UpdateProgress.bind(am));
    },
    UpdateProgress() {
      let am = this;
      // free shipping logic
      let subscription_items = am.cart.items.filter(item => !!item.selling_plan_allocation);
      let free_ship_progress = subscription_items.length ? 100 : (am.cart.total_price * 100 / am.pbars.free_ship.threshold);
      // console.log(am.pbars.free_ship.progress)
      am.pbars.free_ship.progress = free_ship_progress >= 100 ? 100 : free_ship_progress;
    }
  }));

  Alpine.data('CartLineItem', () => ({
    line_item: null,
    product: {},
    upsell: null,
    upsell_plan_name: '',
    cache: {},
    index: 0,
    flags: {
      is_onetime: true,
      product_offers_subscription: false,
      is_3pack: false,
      has_longer_plan_offer: false,
      enable_recommended_upsells: false,
      initiate_fetch_recommended_upsells: false
    },
    recommended_products: [],
    get subDiscountMultiplier() {
      let originalPrice = Number(this.item?.original_price);
      let finalPrice = Number(this.item?.final_price);
      if (!Number.isFinite(originalPrice) || originalPrice <= 0 || !Number.isFinite(finalPrice)) return 1;

      let multiplier = finalPrice / originalPrice;
      return Number.isFinite(multiplier) && multiplier > 0 && multiplier <= 1 ? multiplier : 1;
    },
    init() {
      let am = this;
      am.InitWatchers();
      am.$nextTick(am.InitSelf.bind(am));
    },
    async InitSelf() {
      let am = this;
      am.cache.$cart_drawer = am.$el.closest('.cart_drwr');
      am.index = parseInt(am.$el.dataset.index);

      let line_item = am.cart.items[am.index];
      am.line_item = line_item;

      am.CheckFlags();
      if (am.line_item.handle) {
        // Cache /products/{handle}.js at window level. The product JSON
        // (variants, selling plans, metafields) is static between page
        // loads, so the first cart drawer build-out is the only one that
        // needs to pay the fetch cost. Subsequent cart mutations reuse
        // the cached promise instead of refetching for every line item.
        // Storing the PROMISE (not the resolved value) means concurrent
        // first-call fetches dedupe naturally.
        window.__semaine_product_cache = window.__semaine_product_cache || {};
        let cache = window.__semaine_product_cache;
        if (!cache[am.line_item.handle]) {
          cache[am.line_item.handle] = fetch(`/products/${am.line_item.handle}.js`).then(res => res.json());
        }
        let product = await cache[am.line_item.handle];
        am.product = {...am.product, ...product};
      }

      let variant = am.product.variants.find(v => v.id == am.line_item.id);
      am.product.curr_variant_compareprice = variant.compare_at_price;
    },
    InitWatchers() {
      let am = this;
      am.$watch('product', () => {
        am.CheckFlags();
        am.CheckUpsells();
        if (!am.flags.initiate_fetch_recommended_upsells && am.recommended_products.length == 0) {
          am.flags.initiate_fetch_recommended_upsells = true;
          am.GetRecommendedProducts();
        }
      });

      document.addEventListener('cart:updated', (e) => {
        // Use the LIVE dataset.index rather than the stale am.index — Alpine
        // shifts these when items are added/removed and the cached value
        // can point at the wrong line.
        let current_index = parseInt(am.$el.dataset.index);
        let line_item = e.detail.cart.items[current_index];
        if (!line_item) return;

        // If the variant under this slot didn't change (i.e. only quantity
        // or cart-level totals shifted), skip the full InitSelf — the slow
        // part is the /products/{handle}.js fetch, and the quantity/price
        // updates are already handled by Alpine reactivity binding directly
        // to cart.items[itemIndex] in the x-for. Just sync line_item and
        // refresh derived flags. Avoids N redundant product refetches on
        // every qty tick when the cart has N items.
        if (am.line_item && line_item.id === am.line_item.id && line_item.handle === am.line_item.handle) {
          am.index = current_index;
          am.line_item = line_item;
          am.CheckFlags();
          am.CheckUpsells();
          return;
        }

        // Variant actually changed (upsell swap, revert flow). Full re-init.
        am.InitSelf();
      });
    },
    CheckFlags() {
      let am = this;
      if (!am.line_item || !am.product) return;

      if (am.product && am.product.selling_plan_groups) {
        am.flags.product_offers_subscription = am.product.selling_plan_groups.length !== 0;
        am.flags.is_onetime = am.flags.product_offers_subscription && !am.line_item.selling_plan_allocation;  
      }
      am.flags.is_3pack = am.line_item.title.indexOf('3 Pack') !== -1;
      am.flags.enable_recommended_upsells = document.querySelector('.cart_drwr').dataset.enable_recommended_upsells == 'true';
    },
    CheckUpsells() {
      let am = this;
      if (!am.line_item || !am.product) return;

      let upsell;
      let variant = am.product.variants.find(v => v.id == am.line_item.id);
      if (!variant) return;
      let pack_type = variant.option1.toLowerCase();
      am.flags.has_longer_plan_offer = false;
      am.upsell_plan_name = '';
      /* 
        // Upsell logic 
        // If One-time - upsell same variant subscription
        // If subscription and not 3-pack - upsell 3-pack variant subscription
      */
      
        if (am.flags.is_onetime) {
          upsell = variant;
          upsell.selling_plan = variant.selling_plan_allocations[0];
        }
        else {
          if (pack_type !== '3 pack') {
            let variant_3pack = am.product.variants.length > 1
              ? am.product.variants.find(v => v.option1 == '3 Pack' && v.id != variant.id)
              : null;
            let selling_plan_3pack = variant_3pack?.selling_plan_allocations?.[0];
            if (variant_3pack && selling_plan_3pack) {
              upsell = variant_3pack;
              upsell.selling_plan = selling_plan_3pack;
              am.flags.has_longer_plan_offer = true;
            }
          }
        }
      am.upsell = upsell;
      if (upsell?.selling_plan) {
        let offered_plan = am.product.selling_plan_groups
          ?.flatMap(group => group.selling_plans || [])
          .find(plan => plan.id == upsell.selling_plan.selling_plan_id);
        am.upsell_plan_name = upsell.selling_plan.selling_plan?.name || offered_plan?.name || '';
      }
    },
    async e_AddUpsell() {
      let am = this,
        line_item = am.line_item,
        upsell = am.upsell;
      let $cartDrawer = am.cache.$cart_drawer && Alpine.$data(am.cache.$cart_drawer);
      if ($cartDrawer) $cartDrawer.offerError = '';
      try {
        // If upsell is same as current variant, add subscription
        if (line_item.id == upsell.id) {
          let item_updates = {
            id: line_item.key,
            quantity: line_item.quantity,
            selling_plan: upsell.selling_plan.selling_plan_id,
          };
          // /cart/change.js returns the full cart — apply directly, skip the
          // follow-up GET /cart.js that cart:modified would have triggered.
          let cart = await UtcCartGuard.call('/cart/change.js', item_updates);
          if ($cartDrawer) $cartDrawer.ApplyCart(cart); else document.dispatchEvent(new CustomEvent('cart:modified'));
        }
        // If upsell is not same as current variant, replace variant with new variant & subscription
        if (line_item.id !== upsell.id) {
          // add the new line first so the cart is never left without its anchor,
          // then remove the old line (and any gifts it was anchoring).
          await UtcCartGuard.call('/cart/add.js', { items: [{
            id: upsell.id,
            quantity: 1,
            selling_plan: upsell.selling_plan.selling_plan_id
          }] });
          await UtcCartGuard.removeLines([line_item.key]);
          document.dispatchEvent(new CustomEvent('cart:modified'));
        }
      } catch (e) {
        UtcCartGuard.report(e && e.description);
        document.dispatchEvent(new CustomEvent('cart:modified'));
      }
    },
    async e_Revert_OneTime() {
      let am = this,
        line_item = am.line_item;
      let item_updates = {
        id: line_item.key,
        quantity: line_item.quantity,
        selling_plan: null
      };
      let $cartDrawer = am.cache.$cart_drawer && Alpine.$data(am.cache.$cart_drawer);
      if ($cartDrawer) $cartDrawer.offerError = '';
      try {
        if (UtcCartGuard.isAnchor(line_item)) {
          // Anchor going one-time orphans its gifts, and a gifts-first clear can race the
          // gift reconcile. Remove line + gifts atomically, then add the one-time line.
          await UtcCartGuard.removeLines([line_item.key]);
          await UtcCartGuard.call('/cart/add.js', { items: [{ id: line_item.variant_id, quantity: line_item.quantity }] });
          document.dispatchEvent(new CustomEvent('cart:modified'));
        } else {
          let cart = await UtcCartGuard.call('/cart/change.js', item_updates);
          if ($cartDrawer) $cartDrawer.ApplyCart(cart); else document.dispatchEvent(new CustomEvent('cart:modified'));
        }
      } catch (e) {
        UtcCartGuard.report(e && e.description);
        document.dispatchEvent(new CustomEvent('cart:modified'));
      }
    },
    async e_Revert_Monthly() {
      let am = this,
        line_item = am.line_item;
      let $cartDrawer = am.cache.$cart_drawer && Alpine.$data(am.cache.$cart_drawer);
      if ($cartDrawer) $cartDrawer.offerError = '';
      try {
        // Remove the line and its gifts together in one update.js call.
        await UtcCartGuard.removeLines([line_item.key]);

        let variant_1pack = am.product.variants.find(v => v.option1 == '1 Pack');
        let selling_plan_1pack = variant_1pack.selling_plan_allocations[0];
        await UtcCartGuard.call('/cart/add.js', { items: [{
          id: variant_1pack.id,
          quantity: line_item.quantity,
          selling_plan: selling_plan_1pack.selling_plan_id
        }] });
      } catch (e) {
        UtcCartGuard.report(e && e.description);
      }
      // /cart/add.js doesn't return the full cart — one refresh needed here.
      document.dispatchEvent(new CustomEvent('cart:modified'));
    },
    async GetRecommendedProducts() {
      let am = this;
      if (!am.flags.enable_recommended_upsells) return;
      
      await fetch(window.Shopify.routes.root + `recommendations/products.json?product_id=${am.product.id}&limit=4&intent=related`)
      .then(response => response.json())
      .then(({ products }) => {
        products = products.filter(p => p.available);
        products = products.filter(p => am.cart.items.every(item => String(item.product_id) !== String(p.id) && item.handle !== p.handle));
        am.recommended_products = [...products];
        am.$dispatch('cart-recommended-products-loaded', { products });
      });
    }
  }));

  Alpine.data('RecommendedProducts', () => ({
    init() {
      let am = this;
      am.InitSelf();
    },
    InitSelf() {
      let am = this;
      // am.swiper = new Swiper(am.$el, {
      //   slidesPerView: 1.25,
      //   spaceBetween: 10,
      //   grabCursor: true,
      //   pagination: {
      //     el: '.swiper-pagination',
      //     clickable: true
      //   },
      // });
    },
    async e_AddUpsell(e) {
      // console.log(e);
      let am = this;
      let variant = am.product.variants.find(v => v.available);
      let add_item = {
        id: variant.id,
        quantity: 1,
        selling_plan: variant.selling_plan_allocations[0].selling_plan_id
      };
      await UTILS.AddItemsToCart([add_item]);
      document.dispatchEvent(new CustomEvent('cart:modified'));  
    }
  }));
});
